import { useCallback, useEffect, useState } from "react";
import {
  fetchDealerGovernanceDashboard,
  patchDealerGovernanceFinding,
  runDealerGovernanceChecks,
} from "../../../../services/dealerCoverageApi";
import { useAuth } from "../../../../auth/useAuth";
import { ADMIN_PERMISSIONS } from "../../../../auth/adminPermissions";
import AdminTableContainer from "../layout/AdminTableContainer";

const STATUS_FILTERS = [
  { value: "open", label: "Open" },
  { value: "acknowledged", label: "Acknowledged" },
  { value: "resolved", label: "Resolved" },
  { value: "", label: "All statuses" },
];

const DealerGovernanceAdminPage = ({ onClose, onOpenDealerDetail }) => {
  const { hasPermission, isSuperAdmin } = useAuth();
  const canManage =
    isSuperAdmin || hasPermission(ADMIN_PERMISSIONS.REAL_ESTATE_SOURCES_MANAGE);

  const [dashboard, setDashboard] = useState(null);
  const [statusFilter, setStatusFilter] = useState("open");
  const [loading, setLoading] = useState(true);
  const [running, setRunning] = useState(false);
  const [message, setMessage] = useState("");

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const data = await fetchDealerGovernanceDashboard({
        status: statusFilter || undefined,
        limit: 150,
      });
      setDashboard(data);
    } catch (err) {
      setMessage(err.message || "Failed to load governance dashboard");
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleRun = async (dryRun = true) => {
    if (!canManage) return;
    setRunning(true);
    setMessage("");
    try {
      const result = await runDealerGovernanceChecks({ dryRun });
      setMessage(
        `Governance ${result.report?.status || "done"} — ${result.report?.findingCount || 0} findings (dry-run: ${dryRun ? "yes" : "no"})`
      );
      if (!dryRun) await load();
    } catch (err) {
      setMessage(err.message || "Governance run failed");
    } finally {
      setRunning(false);
    }
  };

  const handlePatch = async (findingId, status) => {
    if (!canManage) return;
    try {
      await patchDealerGovernanceFinding(findingId, { status });
      await load();
    } catch (err) {
      setMessage(err.message || "Update failed");
    }
  };

  const openDetail = (profileKey) => {
    if (!profileKey) return;
    if (onOpenDealerDetail) {
      onOpenDealerDetail(profileKey);
      return;
    }
    window.location.hash = `#/admin/automotive-dealer-detail/${encodeURIComponent(profileKey)}`;
  };

  const findings = dashboard?.findings || [];

  return (
    <div className="admin-page automotive-dealer-governance-page">
      <header className="admin-page__header">
        <div>
          <p className="admin-page__eyebrow">Automotive · Dealer Registry</p>
          <h1>Dealer Governance</h1>
          <p className="muted">Data quality checks for the 100-source dealer registry. No auto-merge actions.</p>
        </div>
        <div className="admin-page__actions">
          <a className="admin-btn admin-btn--ghost" href="#/admin/automotive-dealer-coverage">
            Coverage dashboard
          </a>
          {onClose ? (
            <button type="button" className="admin-btn admin-btn--ghost" onClick={onClose}>
              Close
            </button>
          ) : null}
        </div>
      </header>

      {dashboard?.summary ? (
        <section className="food-comparison-summary-cards">
          <div className="food-comparison-summary-cards__card">
            <span className="muted">Open findings</span>
            <strong>{dashboard.summary.openFindings}</strong>
          </div>
          <div className="food-comparison-summary-cards__card">
            <span className="muted">Critical</span>
            <strong>{dashboard.summary.openCritical}</strong>
          </div>
          <div className="food-comparison-summary-cards__card">
            <span className="muted">Warnings</span>
            <strong>{dashboard.summary.openWarn}</strong>
          </div>
        </section>
      ) : null}

      <section className="admin-filters admin-filters--inline" style={{ marginTop: "1rem" }}>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
          {STATUS_FILTERS.map((f) => (
            <option key={f.value || "all"} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
        <button type="button" className="admin-btn admin-btn--secondary admin-btn--compact" onClick={load}>
          Refresh
        </button>
        {canManage ? (
          <>
            <button
              type="button"
              className="admin-btn admin-btn--secondary admin-btn--compact"
              onClick={() => handleRun(true)}
              disabled={running}
            >
              Dry-run checks
            </button>
            <button
              type="button"
              className="admin-btn admin-btn--compact"
              onClick={() => handleRun(false)}
              disabled={running}
            >
              Run & persist
            </button>
          </>
        ) : null}
      </section>

      {message ? <p className="muted">{message}</p> : null}
      {loading ? <p className="muted">Loading governance findings…</p> : null}

      <AdminTableContainer>
        <h3>Governance findings</h3>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Dealer</th>
              <th>Type</th>
              <th>Severity</th>
              <th>Status</th>
              <th>Action</th>
              <th>Detected</th>
              <th>Ops</th>
            </tr>
          </thead>
          <tbody>
            {findings.map((f) => (
              <tr key={f.id}>
                <td>
                  {f.dealerProfileKey ? (
                    <button
                      type="button"
                      className="admin-link-btn"
                      onClick={() => openDetail(f.dealerProfileKey)}
                    >
                      {f.dealerDisplayName}
                    </button>
                  ) : (
                    f.dealerDisplayName
                  )}
                </td>
                <td>{f.findingType}</td>
                <td>{f.severity}</td>
                <td>{f.status}</td>
                <td>{f.recommendedAction || "—"}</td>
                <td>{f.detectedAt ? new Date(f.detectedAt).toLocaleDateString() : "—"}</td>
                <td>
                  {canManage && f.status === "open" ? (
                    <>
                      <button
                        type="button"
                        className="admin-btn admin-btn--compact"
                        onClick={() => handlePatch(f.id, "acknowledged")}
                      >
                        Ack
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--compact"
                        onClick={() => handlePatch(f.id, "resolved")}
                      >
                        Resolve
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn--compact"
                        onClick={() => handlePatch(f.id, "false_positive")}
                      >
                        Dismiss
                      </button>
                    </>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </AdminTableContainer>
    </div>
  );
};

export default DealerGovernanceAdminPage;
